import { useEffect, useReducer } from 'react';
import Button from 'components/ui-components/Button';
import { useHTTP } from 'hooks/useRequest';
import ResendCodeForm from './ResendCodeForm';

const initialState = {
  resendDisabled: true,
  countdown: 0,
  showForm: false,
  clicked: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_RESEND_DISABLED':
      return { ...state, resendDisabled: action.payload };
    case 'SET_COUNTDOWN':
      return { ...state, countdown: action.payload };
    case 'SET_SHOW_FORM':
      return { ...state, showForm: action.payload };
    case 'SET_CLICKED':
      return { ...state, clicked: action.payload };
    case 'BUTTON_CLICKED':
      return action.payload.requiresPassword
        ? { ...state, showForm: true }
        : { ...state, clicked: true };
    case 'REDUCE_COUNTDOWN':
      return {
        ...state,
        countdown: state.countdown - 1,
        resendDisabled: state.countdown - 1 > 0,
      };
    case 'RESET_COUNTDOWN':
      return {
        ...state,
        countdown: action.payload.seconds,
        resendDisabled: true,
        showForm: false,
      };
    default:
      return state;
  }
};

const ResendCodeButton = ({
  timerDuration,
  username,
  mfaParam,
  requiresPassword,
  showModal,
  children,
  ...rest
}) => {
  const [{ clicked, resendDisabled, countdown, showForm }, dispatch] =
    useReducer(reducer, {
      ...initialState,
      countdown: timerDuration,
    });

  const resendCodeRequestConfig = {
    method: 'POST',
    url: clicked ? '/mycef/mobile_number/resend_verification_code' : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      mfa: mfaParam,
    }),
  };

  const { data: resendCodeResponse, loading } = useHTTP(
    resendCodeRequestConfig || null,
  );

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    dispatch({ type: 'BUTTON_CLICKED', payload: { requiresPassword } });
  };

  const resetCountdown = (seconds, requestLimitReached) => {
    dispatch({ type: 'RESET_COUNTDOWN', payload: { seconds } });
    if (requestLimitReached) showModal();
  };

  useEffect(() => {
    dispatch({ type: 'SET_CLICKED', payload: false });

    if (resendCodeResponse?.success) {
      resetCountdown(
        resendCodeResponse?.secondsUntilNextRequest,
        resendCodeResponse?.requestLimitReached,
      );
    }
  }, [resendCodeResponse]);

  useEffect(() => {
    let timer;
    if (resendDisabled) {
      timer = setTimeout(() => {
        dispatch({ type: 'REDUCE_COUNTDOWN' });
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  return !showForm ? (
    <Button {...rest} handleClick={handleClick} disabled={resendDisabled}>
      {loading ? (
        <span className="cef-btn__loader bs-bb mlr-20" />
      ) : (
        <>
          {children}
          {resendDisabled && ` (${formatTime(countdown)})`}
        </>
      )}
    </Button>
  ) : (
    <ResendCodeForm username={username} resetCountdown={resetCountdown} />
  );
};

export default ResendCodeButton;
